/**
 * ONNI Warenkorb-Extras
 * ---------------------
 * Zwei Aufgaben, mehr nicht:
 *   1. Geschenk (STYLING Brush) ab Schwelle automatisch rein und wieder raus.
 *   2. Zusatzverkauf-Buttons im Drawer.
 *
 * Den Fortschrittsbalken zeichnet Liquid, nicht dieses Skript. Horizon
 * rendert die Drawer-Section nach jeder Warenkorb-Aenderung neu, damit
 * stimmt die Anzeige ohne eine Zeile JS.
 *
 * Auftrag Paavo 28.07.2026.
 */
import { morphSection } from '@theme/section-renderer';
import { CartUpdateEvent } from '@theme/events';

const CART_UPDATE = 'cart:update';

/** Verhindert, dass unsere eigene Aenderung sich selbst erneut ausloest. */
let laeuft = false;

/**
 * Warenkorb ueber den gemeinsamen Leser aus layout/theme.liquid (Code-Review A-05).
 * @returns {Promise<any>}
 */
function korbHolen() {
  if (typeof window.onniKorb === 'function') return window.onniKorb();
  return fetch(`${Theme.routes.cart_url}.js`).then((r) => (r.ok ? r.json() : null));
}

/** @returns {HTMLElement | null} */
function extrasKnoten() {
  return document.querySelector('[data-onni-cart-extras]');
}

/** @returns {string | null} Section-ID des Drawers, fuer das Nachrendern. */
function drawerSectionId() {
  const el = document.querySelector('cart-items-component[data-drawer]');
  return el instanceof HTMLElement ? el.dataset.sectionId ?? null : null;
}

/**
 * Drawer neu rendern lassen, damit Balken, Geschenkzeile und Summe stimmen.
 *
 * Wichtig: Wir lassen Shopify die Section gleich im selben Aufruf
 * mitliefern (Parameter `sections`), genau wie component-cart-items.js.
 * Ein eigener Aufruf mit `?section_id=` liefert die Drawer-Section des
 * Headers nicht zurueck und morphSection findet dann nichts.
 *
 * @param {{sections?: Record<string, string>}} antwort
 * @param {string | null} sectionId
 */
async function neuRendern(antwort, sectionId) {
  if (!sectionId) return;
  const html = antwort?.sections?.[sectionId];
  if (!html) return;
  await morphSection(sectionId, html, 'hydration');
}

/**
 * Geschenk anhand des Warenwerts setzen oder entfernen.
 * Der Warenwert rechnet das Geschenk selbst heraus, sonst haelt es sich
 * ueber seinen eigenen Preis oberhalb der Schwelle am Leben.
 */
async function geschenkAbgleichen() {
  const knoten = extrasKnoten();
  if (!knoten || laeuft) return;

  const varianteId = Number(knoten.dataset.geschenkVariante);
  const schwelle = Number(knoten.dataset.geschenkSchwelle);
  if (!varianteId || !schwelle) return;

  const cart = await korbHolen();
  const geschenkZeile = cart.items.findIndex((i) => i.variant_id === varianteId);
  const warenwert = cart.items
    .filter((i) => i.variant_id !== varianteId)
    .reduce((summe, i) => summe + i.final_line_price, 0);

  const sollDrin = warenwert >= schwelle;
  const istDrin = geschenkZeile !== -1;
  if (sollDrin === istDrin) return;

  const sectionId = drawerSectionId();

  laeuft = true;
  try {
    /** @type {{sections?: Record<string, string>}} */
    let antwort = {};
    if (sollDrin) {
      antwort = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [
            {
              id: varianteId,
              quantity: 1,
              properties: { Geschenk: `gratis ab ${schwelle / 100} ${cart.currency}` },
            },
          ],
          sections: sectionId,
          sections_url: window.location.pathname,
        }),
      }).then((r) => r.json());
    } else {
      antwort = await fetch(Theme.routes.cart_change_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          line: geschenkZeile + 1,
          quantity: 0,
          sections: sectionId,
          sections_url: window.location.pathname,
        }),
      }).then((r) => r.json());
    }
    await neuRendern(antwort, sectionId);
  } catch (fehler) {
    console.error('[onni-cart-extras] Geschenk konnte nicht gesetzt werden', fehler);
  } finally {
    laeuft = false;
  }
}

/**
 * Zusatzverkauf: Klick auf "Hinzufuegen" legt die Variante in den Warenkorb.
 * @param {MouseEvent} event
 */
async function upsellKlick(event) {
  const ziel = event.target;
  if (!(ziel instanceof HTMLElement)) return;
  const button = ziel.closest('[data-onni-upsell-add]');
  if (!(button instanceof HTMLButtonElement)) return;

  const varianteId = Number(button.dataset.onniUpsellAdd);
  if (!varianteId) return;

  const sectionId = drawerSectionId();
  button.disabled = true;
  upsellMeldung(button, '');
  try {
    const r = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [{ id: varianteId, quantity: 1 }],
        sections: sectionId,
        sections_url: window.location.pathname,
      }),
    });
    const antwort = await r.json();
    /* Code-Review D-05 (Johannes 20.10.2026): Bei 422 (nicht kaufbar, Mengenlimit) kam vorher
       nichts zurueck und der Knopf blieb stumm grau. Jetzt Shopifys Meldung zeigen, Knopf frei. */
    if (!r.ok) {
      upsellMeldung(button, antwort?.description || antwort?.message || 'Konnte nicht hinzugefügt werden.');
      button.disabled = false;
      return;
    }
    /* Code-Review D-05: Horizon das Einlegen melden wie product-form.js, damit Header-Zaehler,
       Vorlesetext, Karten-Status und der Drawer selbst nachziehen. Der Drawer rendert dabei aus
       den mitgelieferten Sections, deshalb kein eigenes neuRendern mehr. */
    const cart = await korbHolen();
    document.dispatchEvent(
      new CartUpdateEvent(cart, 'onni-upsell', {
        source: 'onni-upsell',
        itemCount: cart?.item_count ?? 0,
        variantId: String(varianteId),
        sections: antwort?.sections,
      })
    );
  } catch (fehler) {
    console.error('[onni-cart-extras] Zusatzverkauf fehlgeschlagen', fehler);
    button.disabled = false;
  }
}

/**
 * Meldung unter einer Upsell-Karte zeigen oder leeren (Code-Review D-05).
 * @param {HTMLElement} button
 * @param {string} text
 */
function upsellMeldung(button, text) {
  const karte = button.closest('.onni-upsell__karte');
  if (!karte) return;
  let feld = karte.querySelector('.onni-upsell__fehler');
  if (!text) {
    feld?.remove();
    return;
  }
  if (!feld) {
    feld = document.createElement('p');
    feld.className = 'onni-upsell__fehler';
    feld.setAttribute('role', 'alert');
    feld.style.cssText = 'margin:4px 0 0;font-size:12px;line-height:1.3;color:#b3261e;grid-column:1/-1;';
    karte.appendChild(feld);
  }
  feld.textContent = text;
}

/**
 * Vorlesetext der Warenkorb-Blase nachziehen.
 *
 * cart-icon.js aktualisiert nur die sichtbare Zahl. Der versteckte Text
 * "Artikel im Warenkorb insgesamt: 0" kommt vom Server und bleibt nach einem
 * AJAX-Hinzufuegen stehen, bis die Seite komplett neu laedt. Befund
 * Chef-LENA 31.07.2026.
 */
async function vorlesetextNachziehen() {
  const felder = document.querySelectorAll('.cart-bubble__text > .visually-hidden');
  if (!felder.length) return;

  const cart = await korbHolen();
  for (const feld of felder) {
    const beschriftung = (feld.textContent ?? '').split(':')[0].trim();
    feld.textContent = `${beschriftung}: ${cart.item_count}`;
  }
}

document.addEventListener(CART_UPDATE, () => {
  geschenkAbgleichen();
  vorlesetextNachziehen();
});
document.addEventListener('click', upsellKlick);

// Beim ersten Laden einmal abgleichen: der Warenkorb kann aus einer
// frueheren Sitzung ueber oder unter der Schwelle liegen.
geschenkAbgleichen();
